export const mergeAdminMessages = (current, incoming) => {
  const byId = new Map(current.map(message => [message.id, message]))
  incoming.filter(Boolean).forEach(message => byId.set(message.id, { ...byId.get(message.id), ...message }))
  return [...byId.values()].sort((left, right) => (left.createdAt || 0) - (right.createdAt || 0))
}

export const mergeAdminQueueMeta = (conversations, meta) => {
  const metaById = new Map((meta || []).map(item => [item.id, item]))
  return conversations
    .map(item => ({ ...item, ...metaById.get(item.id) }))
    .filter(item => ['WAITING_ADMIN', 'LIVE', 'CLOSED', 'TIMED_OUT'].includes(item.status))
    .sort((left, right) => (right.lastMessageAt || 0) - (left.lastMessageAt || 0))
}

export const mergeClosedAdminConversation = (conversations, closedConversation) => {
  const exists = conversations.some(item => item.id === closedConversation.id)
  const merged = exists
    ? conversations.map(item => item.id === closedConversation.id
      ? { ...item, ...closedConversation }
      : item)
    : [...conversations, closedConversation]
  return merged.sort((left, right) => (right.lastMessageAt || 0) - (left.lastMessageAt || 0))
}

export const getAdminConversationStatusLabel = status => {
  if (status === 'WAITING_ADMIN') return 'Waiting for admin'
  if (status === 'LIVE') return 'Live'
  if (status === 'CLOSED') return 'Closed'
  if (status === 'TIMED_OUT') return 'Timed out'
  return status || 'Unknown'
}

export const getAdminConversationStatusPresentation = status => {
  if (status === 'WAITING_ADMIN') {
    return {
      label: 'Waiting for admin',
      badgeClassName: 'border-amber-300 bg-amber-50 text-amber-700',
      dotClassName: 'bg-amber-500 animate-pulse'
    }
  }
  if (status === 'LIVE') {
    return {
      label: 'Active',
      badgeClassName: 'border-emerald-300 bg-emerald-50 text-emerald-700',
      dotClassName: 'bg-emerald-500 animate-pulse'
    }
  }
  if (status === 'TIMED_OUT') {
    return {
      label: 'Timed out',
      badgeClassName: 'border-violet-300 bg-violet-50 text-violet-700',
      dotClassName: 'bg-violet-500'
    }
  }
  return {
    label: status === 'CLOSED' ? 'Closed' : status || 'Unknown',
    badgeClassName: 'border-gray-300 bg-gray-100 text-gray-600',
    dotClassName: 'bg-gray-400'
  }
}

export const getAdminQueueRequestForFilter = filter => {
  if (filter === 'ALL') return { scope: 'ACTIVE' }
  if (filter === 'HISTORY') return { scope: 'HISTORY' }
  return { status: filter }
}

export const getAdminQueueStatusForFilter = filter => getAdminQueueRequestForFilter(filter).status

export const filterAdminConversations = (conversations, filter) => conversations.filter(item => {
  if (filter === 'ALL') {
    return item.status === 'WAITING_ADMIN' || item.status === 'LIVE'
  }
  if (filter === 'HISTORY') {
    return item.status === 'CLOSED' || item.status === 'TIMED_OUT'
  }
  return item.status === filter
})

export const getTranscriptEmailPresentation = status => {
  if (status === 'PENDING') return { label: 'Email queued', className: 'text-amber-700' }
  if (status === 'SENDING') return { label: 'Sending transcript…', className: 'text-blue-700' }
  if (status === 'SENT') return { label: 'Transcript emailed', className: 'text-emerald-700' }
  if (status === 'FAILED') return { label: 'Transcript email failed', className: 'text-red-700' }
  if (status === 'UNKNOWN') return { label: 'Email delivery uncertain', className: 'text-violet-700' }
  return undefined
}

export const getAdminChatBudgetPresentation = status => status === 'OPEN'
  ? {
      title: 'Admin-only routing active',
      description: 'Gemini and automated answers are paused. New chats and existing bot chats are sent to the admin inbox. Resuming bot assistance will not remove existing chats from the admin queue.',
      action: 'Resume bot for new chats',
      adminOnly: true
    }
  : {
      title: 'Bot assistance active',
      description: 'Gemini and automated answers can handle new chats. Questions that cannot be resolved are sent to admins.',
      action: 'Pause bot and route chats to admins',
      adminOnly: false
    }
